class AutoplayVideo extends HTMLElement {
  /** @type {IntersectionObserver | null} */
  #observer = null;

  /** @type {AbortController | null} */
  #abortController = null;

  connectedCallback() {
    const video = this.querySelector('video');
    if (!(video instanceof HTMLVideoElement)) return;

    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    this.#prepare(video);
    this.#play(video);

    video.addEventListener('canplay', () => this.#play(video), { signal });
    video.addEventListener('loadeddata', () => this.#play(video), { signal });

    window.addEventListener('pageshow', () => this.#play(video), { signal });
    document.addEventListener(
      'visibilitychange',
      () => {
        if (document.visibilityState === 'visible') this.#play(video);
      },
      { signal }
    );
    document.addEventListener('pointerdown', () => this.#play(video), { capture: true, signal });
    document.addEventListener('touchstart', () => this.#play(video), { capture: true, signal });
    document.addEventListener('keydown', () => this.#play(video), { capture: true, signal });

    if ('IntersectionObserver' in window) {
      this.#observer = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (entry.isIntersecting) this.#play(video);
          }
        },
        { threshold: 0.01 }
      );
      this.#observer.observe(video);
    }
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.#observer = null;
    this.#abortController?.abort();
    this.#abortController = null;
  }

  /**
   * @param {HTMLVideoElement} video
   */
  #prepare(video) {
    video.muted = true;
    video.defaultMuted = true;
    video.volume = 0;
    video.playsInline = true;
    video.autoplay = true;
    video.loop = true;
    video.controls = false;
    video.preload = 'auto';
    video.setAttribute('muted', '');
    video.setAttribute('playsinline', '');
    video.setAttribute('webkit-playsinline', '');
    video.setAttribute('autoplay', '');

    if (!video.dataset.hlsStripped) {
      let strippedHls = false;
      for (const source of video.querySelectorAll('source')) {
        const type = source.getAttribute('type') || '';
        const src = source.getAttribute('src') || source.dataset.videoSource || '';
        if (type.includes('mpegURL') || src.includes('.m3u8')) {
          source.remove();
          strippedHls = true;
        }
      }

      const videoSrc = video.getAttribute('src') || '';
      if (videoSrc.includes('.m3u8')) {
        video.removeAttribute('src');
        strippedHls = true;
      }

      video.dataset.hlsStripped = 'true';
      if (strippedHls) video.load();
    }
  }

  /**
   * @param {HTMLVideoElement} video
   */
  #play(video) {
    this.#prepare(video);
    video.play()?.catch(() => {});
  }
}

if (!customElements.get('autoplay-video')) {
  customElements.define('autoplay-video', AutoplayVideo);
}
